// Revise ▸ History (the Mac's CheckpointsColumn and HistoryPage): the checkpoints, newest first, and what's changed
// since the chosen one — against today's draft or a later checkpoint (spec §6.3, `ProjectDiff`): words net, written
// and cut; scenes revised, added, cut, moved, split and merged; each changed scene word by word (new words in
// verdigris, cut words struck through in terracotta, moved sentences faint, long unchanged stretches shortened); and
// words by POV and status. Restoring is the Mac's (History ▸ Restore); this page says so.
//
// The comparison runs in the Revise room's worker (store/reviseHost.ts), which keeps its own copy of the documents: at
// 150,000 words it takes ≈ 750 ms (the checkpoint's scenes read at their heads, the book's words counted), none of it on
// this thread, so typing and the page never wait for it. The page sends only what changed and draws the result.
import { useEffect, useMemo, useState } from 'react';
import { diffCounts, netWords, type OpenedProject, type ProjectDiff, type Segment } from '@writing-suite/core';
import type { PageProps } from '../shell/ProjectShell.tsx';
import { useWorkspace } from '../shell/workspace.ts';
import { reviseClient } from '../store/reviseClient.ts';
import { formatNumber } from './helpers.ts';
import type { AttributedDiff } from '../store/reviseHost.ts';
import { madeBy, membersOf } from '../store/members.ts';
import { AuthorsLine, PersonDot } from './People.tsx';

const WHEN = new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
const LONG = new Intl.DateTimeFormat('en-GB', { dateStyle: 'full', timeStyle: 'short' });
const plural = (n: number, one: string, many = `${one}s`) => `${formatNumber(n)} ${n === 1 ? one : many}`;

export function HistoryPage({ project, live }: PageProps) {
  const ws = useWorkspace();
  const opened = project as OpenedProject;
  const checkpoints = useMemo(() => [...(opened.record?.checkpoints ?? [])].reverse(), [opened]);
  const [fromId, setFromId] = useState<string | null>(null);
  const [toId, setToId] = useState<string | null>(null);
  const from = checkpoints.find((c) => c.id === fromId) ?? checkpoints[0] ?? null;
  const to = checkpoints.find((c) => c.id === toId && c !== from) ?? null;
  const [diff, setDiff] = useState<{ key: string; diff: AttributedDiff } | { key: string; error: string } | null>(null);
  // A shared project's checkpoints and changes are named by who made them (W9c).
  const members = membersOf(live);
  const projectDoc = live?.doc('project');
  const [open, setOpen] = useState<string | null>(null);
  const key = `${from?.id ?? ''}|${to?.id ?? ''}`;

  // Worked out in the worker after the page is drawn, again when the draft changes (debounced); a newer request's answer
  // wins over an older one still on its way.
  useEffect(() => {
    if (!from || !live) return;
    let current = true;
    const t = setTimeout(() => {
      reviseClient(live)
        .history(from.id, to?.id ?? null, opened.manuscriptId())
        .then(
          (d) => current && setDiff({ key, diff: d }),
          (e: unknown) => current && setDiff({ key, error: e instanceof Error ? e.message : String(e) }),
        );
    }, diff?.key === key ? 400 : 0);
    return () => {
      current = false;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, live, to ? null : project]);
  useEffect(() => setOpen(null), [key]);

  const current = diff?.key === key ? diff : null;
  return (
    <div className="history-page">
      <header className="page-head">
        <h1 tabIndex={-1}>History</h1>
        <p className="page-sub">A checkpoint is the whole book as it was. Choose one to see what’s changed since.</p>
      </header>
      <div className="history-layout">
        <nav className="checkpoint-list" aria-label="Checkpoints">
          {checkpoints.length ? (
            <ul>
              {checkpoints.map((c) => (
                <li key={c.id}>
                  <button type="button" className="checkpoint-row" aria-pressed={c === from} onClick={() => setFromId(c.id)} data-checkpoint-id={c.id}>
                    <span className="checkpoint-name">{c.name || 'Untitled'}</span>
                    <span className="checkpoint-date">{WHEN.format(c.createdAt)}</span>
                    {members ? <CheckpointBy by={madeBy(members, projectDoc, ['checkpoints', c.id])} fallback={c.createdBy} /> : null}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          <button type="button" className="link-button" onClick={() => ws?.openSheet('checkpoint')} disabled={!ws?.canWrite}>
            + Make a checkpoint
          </button>
        </nav>
        <section className="history-diff" aria-live="polite" aria-busy={!!from && !current}>
          {!from ? (
            <div className="page-message">
              <h2>No checkpoints yet</h2>
              <p className="lede">Make one from the column (or ⌘S). On the Mac every compile records one too.</p>
            </div>
          ) : (
            <>
              <p className="eyebrow">{LONG.format(from.createdAt)}</p>
              <h2 className="history-title">Since “{from.name || 'Untitled'}”</h2>
              <div className="history-compare">
                <label htmlFor="history-to">Compared with</label>
                <select id="history-to" value={to?.id ?? ''} onChange={(e) => setToId(e.target.value || null)}>
                  <option value="">Today’s draft</option>
                  {checkpoints
                    .filter((c) => c !== from)
                    .map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name || 'Untitled'}
                      </option>
                    ))}
                </select>
              </div>
              <p className="restore-note">To put text back as it was at this checkpoint, use Restore on your Mac (Revise ▸ History).</p>
              {!current ? (
                <p className="pending">Comparing…</p>
              ) : 'error' in current ? (
                <p className="error" role="alert">
                  {current.error}
                </p>
              ) : (
                <DiffView diff={current.diff} project={opened} open={open} setOpen={setOpen} onOpenScene={(id) => ws?.reveal({ fragmentId: id })} />
              )}
            </>
          )}
        </section>
      </div>
    </div>
  );
}

/** Who made a checkpoint: the member whose device recorded it, else the name it was signed with. */
function CheckpointBy({ by, fallback }: { by: { name: string; color: string; known: boolean } | null; fallback: string }) {
  const name = by?.known ? by.name : fallback || by?.name || '';
  if (!name) return null;
  return (
    <span className="checkpoint-by" data-checkpoint-by={name}>
      {by?.known ? <PersonDot color={by.color} /> : null}
      by {name}
    </span>
  );
}

function DiffView({ diff, project, open, setOpen, onOpenScene }: { diff: AttributedDiff; project: OpenedProject; open: string | null; setOpen(id: string | null): void; onOpenScene(id: string): void }) {
  const c = diffCounts(diff);
  const parts = ([[c.revised, 'revised'], [c.added, 'added'], [c.cut, 'cut'], [c.moved, 'moved'], [c.split, 'split'], [c.merged, 'merged']] as const).filter(([n]) => n > 0);
  const net = netWords(diff);
  const sceneAuthors = new Map(diff.authors?.byFragment ?? []);
  const largest = Math.max(1, ...diff.revised.map((r) => r.diff.wordsAdded + r.diff.wordsDeleted));
  const fields = [...diff.byField].sort(([a], [b]) => (a < b ? -1 : 1)).filter(([, values]) => [...values.keys()].some((v) => v !== '—'));
  return (
    <div className="diff" data-words-added={diff.grossAdded} data-words-cut={diff.grossDeleted} data-net={net}>
      <div className="diff-summary">
        <p className="diff-stat">
          <span className="diff-figure">{net === 0 ? '0' : `${net > 0 ? '+' : '−'}${formatNumber(Math.abs(net))}`}</span>
          <span className="diff-label">words net</span>
        </p>
        <p className="diff-stat written">
          <span className="diff-figure">{formatNumber(diff.grossAdded)}</span>
          <span className="diff-label">written</span>
        </p>
        <p className="diff-stat cut">
          <span className="diff-figure">{formatNumber(diff.grossDeleted)}</span>
          <span className="diff-label">cut</span>
        </p>
        <p className="diff-scenes">
          {parts.length ? parts.map(([n, what]) => `${n} ${what}`).join(' · ') : 'No scenes changed'}
          <br />
          <span className="diff-label">
            {formatNumber(diff.wordsBefore)} → {plural(diff.wordsAfter, 'word')}
          </span>
        </p>
      </div>
      {diff.authors ? <AuthorsLine people={diff.authors.total} /> : null}
      {diff.missingFragments.length ? <p className="missing-note">{plural(diff.missingFragments.length, 'scene')} not yet on this computer, so not compared.</p> : null}
      {diff.revised.length ? (
        <section aria-labelledby="scenes-changed">
          <h3 id="scenes-changed" className="section-title">
            Scenes changed
          </h3>
          <ul className="scene-changes">
            {diff.revised.map((r) => (
              <li key={r.id} data-fragment-id={r.id}>
                <button type="button" className="scene-change" aria-expanded={open === r.id} onClick={() => setOpen(open === r.id ? null : r.id)} aria-label={`${diff.title(r.id)}: ${plural(r.diff.wordsAdded, 'word')} written, ${formatNumber(r.diff.wordsDeleted)} cut`}>
                  <span className="chevron" aria-hidden="true">
                    {open === r.id ? '▾' : '▸'}
                  </span>
                  <span className="scene-change-title">{diff.title(r.id)}</span>
                  <span className="change-bar" aria-hidden="true">
                    <span className="bar-added" ref={(el) => el?.style.setProperty('--w', String(r.diff.wordsAdded / largest))} />
                    <span className="bar-cut" ref={(el) => el?.style.setProperty('--w', String(r.diff.wordsDeleted / largest))} />
                  </span>
                  <span className="plus">+{formatNumber(r.diff.wordsAdded)}</span>
                  <span className="minus">−{formatNumber(r.diff.wordsDeleted)}</span>
                </button>
                {sceneAuthors.get(r.id)?.length ? <AuthorsLine people={sceneAuthors.get(r.id)!} className="scene-authors" /> : null}
                {open === r.id ? (
                  <div className="scene-diff">
                    <p className="scene-diff-text">
                      <SceneDiff segments={r.diff.segments} />
                    </p>
                    <button type="button" className="link-button" onClick={() => onOpenScene(r.id)}>
                      Open in Write →
                    </button>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {diff.structural.length ? (
        <section aria-labelledby="structure-changed">
          <h3 id="structure-changed" className="section-title">
            Structure
          </h3>
          <ul className="structure-changes">
            {diff.structural.map((s, i) => (
              <li key={i}>
                {s.type === 'added' ? (
                  <>Added <b>{diff.title(s.id)}</b> at position {s.at + 1}</>
                ) : s.type === 'cut' ? (
                  <>Cut <b>{diff.title(s.id)}</b> (was position {s.from + 1})</>
                ) : s.type === 'moved' ? (
                  <>Moved <b>{diff.title(s.id)}</b> from {s.from + 1} to {s.to + 1}</>
                ) : s.type === 'split' ? (
                  <>Split <b>{diff.title(s.id)}</b> out of <b>{diff.title(s.source)}</b></>
                ) : (
                  <>Merged <b>{diff.title(s.id)}</b> into <b>{diff.title(s.into)}</b></>
                )}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {fields.map(([field, values]) => {
        const name = project.fields().find((f) => f.id === field)?.name ?? field;
        return (
          <section key={field} aria-label={`By ${name.toLowerCase()}`}>
            <h3 className="section-title">By {name.toLowerCase()}</h3>
            <table className="by-field">
              <tbody>
                {[...values].sort(([a], [b]) => (a < b ? -1 : 1)).map(([value, t]) => (
                  <tr key={value}>
                    <th scope="row">{value === '—' || !value ? 'None' : project.displayValue(field, { type: 'text', value })}</th>
                    <td className="plus">+{formatNumber(t.added)}</td>
                    <td className="minus">−{formatNumber(t.deleted)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        );
      })}
    </div>
  );
}

/** Keeps the words next to a change and elides the rest of a long unchanged stretch (`shortened`). */
function shortened(s: string, first: boolean, last: boolean): string {
  const words = s.split(' ');
  const keep = 14;
  if (words.length <= keep * 2 + 4) return s;
  const head = first ? [] : words.slice(0, keep);
  const tail = last ? [] : words.slice(-keep);
  const middle = !head.length && !tail.length ? ' … ' : !head.length ? '… ' : !tail.length ? ' …' : ' … ';
  return head.join(' ') + middle + tail.join(' ');
}

/** One scene's changes as the Mac draws them: inserted words, deleted words struck through, moved sentences faint. */
function SceneDiff({ segments }: { segments: Segment[] }) {
  return (
    <>
      {segments.map((s, i) => {
        switch (s.type) {
          case 'equal':
            return <span key={i} className="diff-equal">{shortened(s.text, i === 0, i === segments.length - 1)}</span>;
          case 'inserted':
            return <ins key={i}>{s.text}</ins>;
          case 'deleted':
            return <del key={i}>{s.text}</del>;
          case 'movedHere':
            return <span key={i} className="diff-moved" title="Moved here">{s.text}</span>;
          case 'movedAway':
            return null;
        }
      })}
    </>
  );
}
